import { useQuery } from '@apollo/client';
import { ButtonSize, ButtonVariant, withTheme } from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import * as React from 'react';
import { AbTestContext } from '../../common/contexts/AbTestContext';
import { TranslationsContext, UserContext } from '../../common/contexts/index';
import { IQuery, ISale, RecommendationSource } from '../../common/types/apiSchema';
import {
  ElementName,
  ElementType,
  ErrorKey,
  ErrorType,
  IContentElementProps,
  ISaleWithId,
} from '../../common/utils/tracking/tracking.interfaces';
import { trackContentRendered, useErrorTracking } from '../../common/utils/tracking/trackingUtils';
import HorizontalSaleList from '../HorizontalSaleList/HorizontalSaleList';
import {
  GET_RECOMMENDED_FOR_YOU_SALES,
  IRecommendedForYouQueryVariables,
} from '../RecommendedForYou/RecommendedForYou.queries';
import { IRecommendedSalesProps } from './RecommendedSales.interfaces';
import { ViewAllSalesButton, ViewAllSalesButtonWrapper } from './RecommendedSales.styles';

const recommendedSalesTrackingId: IContentElementProps = {
  elementName: ElementName.RECOMMENDED_FOR_YOU,
  elementType: ElementType.EXPIRED_SALE,
};

/**
 * Fetches recommended sales from the API, and if successful it renders a horizontal sale card list
 */
const RecommendedSales: React.FunctionComponent<IRecommendedSalesProps> = props => {
  const { userId } = React.useContext(UserContext);
  const { translations } = React.useContext(TranslationsContext);
  const { recommendationSource = RecommendationSource.athena_i2i } = React.useContext(
    AbTestContext
  );

  const { loading, error, data } = useQuery<IQuery, IRecommendedForYouQueryVariables>(
    GET_RECOMMENDED_FOR_YOU_SALES(),
    {
      variables: {
        affiliate: props.affiliate.urlString,
        isWhiteLabel: props.affiliate.isWhiteLabel,
        saleId: props.saleId,
        source: recommendationSource,
        territory: props.territory,
        userId: userId ? userId : '',
      },
    }
  );

  React.useEffect(() => {
    if (data && data.recommendations && data.recommendations.length > 0) {
      const sales: ISaleWithId[] = data.recommendations.map((s: { id: string }) => ({
        saleId: s.id,
      }));
      trackContentRendered(recommendedSalesTrackingId, { sales });
    }
  }, [data]);

  useErrorTracking(ErrorKey.RECOMMENDED_SALES_LOAD, ErrorType.LOADING_ERROR, error);

  if (error) {
    return null;
  }

  const { affiliate } = props;
  const recommendations: ISale[] = data ? data.recommendations : null;

  return (
    <div data-testid="recommendedSales">
      <HorizontalSaleList
        title={i18n('recommendedSales.title', [], translations)}
        affiliate={affiliate}
        sales={recommendations}
        loading={loading}
      />
      <ViewAllSales viewAllSalesUrl={props.viewAllSalesUrl} />
    </div>
  );
};

const ViewAllSales: React.FC<{ viewAllSalesUrl: string }> = ({ viewAllSalesUrl }) => {
  const { translations } = React.useContext(TranslationsContext);

  /**
   * Redirects the window to the view all sales url
   */
  const viewAllSalesClickHandler: () => void = () => {
    window.location.assign(viewAllSalesUrl);

    return;
  };

  return (
    <ViewAllSalesButtonWrapper href={viewAllSalesUrl}>
      <ViewAllSalesButton
        onClick={viewAllSalesClickHandler}
        variant={ButtonVariant.primary}
        size={ButtonSize.medium}
      >
        {`${i18n('common.viewOursales', [], translations)}`}
      </ViewAllSalesButton>
    </ViewAllSalesButtonWrapper>
  );
};

export default withTheme(RecommendedSales);
